import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Popover, PopoverType, PopoverSelector, ScrollableList, ScrollableListVariant, Price, PriceSize, usePopoverHandlers, } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { MainWebShop, } from '@as-react/common-types';
import { findColor, determineSelectedSku } from '@as-react/common-utils';
import { useMediaQueryMatchesOnMobile, useMediaQueryMatchesOnDesktop, Box, Breakpoint, Stack, TextAlpha, } from '@yonderland/andes-react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ScrollUtil } from '../../../util/ScrollUtil';
import AsNumeral from '../../../vendors/asNumeral';
import { MiniPDPDeliveryPromise } from './MiniPDPDeliveryPromise';
import { MiniPDPExtensiveImageCarousel } from './MiniPDPExtensiveImageCarousel';
import { MiniPDPMultiCTA } from './MiniPDPMultiCTA';
import MiniPDPMultiFooter from './MiniPDPMultiFooter';
import { MiniPDPMultiProductTileList } from './MiniPDPMultiProductTileList';
export const MiniPDPMulti = ({ isOpen, popoverTitle, title, onClose, products, extensiveCarouselImages, wishlistProducts, deliveryPromise, cdnSettings, totalPrice, isPDP, onWishListToggle, onAddToBasket, fetchDeliveryPromise, fetchTotalPrice, }) => {
    const tilesWrapperRef = useRef(null);
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const aemConfiguration = useAemConfig();
    const { currency, isShopLanding, mainWebShop, feature_toggles, imageCdnPrefix } = aemConfiguration;
    const { show_sticky_bar_pdp } = feature_toggles;
    const productsCount = products.length;
    const getInitSelectedProductsData = () => products.map(product => {
        const activeColor = findColor(product.colours, product.colourId);
        return {
            product,
            colorId: product.colourId,
            sku: determineSelectedSku(activeColor),
            quantity: 1,
            isSelected: false,
        };
    });
    const isMobile = useMediaQueryMatchesOnMobile();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isTablet = !isMobile && !isDesktop;
    const { anchorEl, handlePopoverOpen, handlePopoverClose } = usePopoverHandlers({ close: onClose });
    const [selectedProductsData, setSelectedProductsData] = useState(getInitSelectedProductsData());
    const [sizeSelectErrors, setSizeSelectErrors] = useState(new Array(productsCount).fill(false));
    const onProductChange = (index, data) => {
        const newSelectedProductsData = [...selectedProductsData];
        newSelectedProductsData[index] = data;
        setSelectedProductsData(newSelectedProductsData);
    };
    const handleAddToBasket = () => {
        const sizeErrors = selectedProductsData.map(item => (item.isSelected ? !item.sku : false));
        if (sizeErrors.some(error => error)) {
            setSizeSelectErrors(sizeErrors);
            const firstErrorIndex = sizeErrors.findIndex(item => item);
            if (isPDP && !isMobile && tilesWrapperRef.current) {
                const element = tilesWrapperRef.current.querySelectorAll('.as-m-product-tile')[firstErrorIndex];
                const scrollingOffset = ScrollUtil.getScrollingOffset(isDesktop, isTablet, show_sticky_bar_pdp);
                ScrollUtil.scrollToPosition(element.offsetTop - scrollingOffset);
            }
            else {
                const elementInPopover = document.querySelectorAll(`.${PopoverSelector.MODAL} .as-m-product-tile`)[firstErrorIndex];
                ScrollUtil.scrollToElement(elementInPopover, true);
            }
        }
        else {
            onAddToBasket(selectedProductsData.filter(item => item.isSelected));
            handlePopoverClose();
            setSelectedProductsData(getInitSelectedProductsData());
        }
    };
    const handleSetError = (error, index) => {
        setSizeSelectErrors(items => {
            const sizeErrors = [...items];
            sizeErrors[index] = error;
            return sizeErrors;
        });
    };
    useEffect(() => {
        if (isOpen) {
            handlePopoverOpen(null, null, true, true);
        }
        else {
            setSizeSelectErrors(new Array(productsCount).fill(false));
            handlePopoverClose();
        }
    }, [isOpen]);
    useEffect(() => {
        fetchDeliveryPromise(selectedProductsData.filter(product => product.isSelected && product.colorId && product.sku));
        fetchTotalPrice(selectedProductsData.filter(product => product.isSelected && product.colorId && product.sku));
    }, [selectedProductsData]);
    const numberProducts = selectedProductsData.filter(item => item.isSelected).length;
    const getTitleTranslation = () => {
        if (!isPDP) {
            return t('product.look.title');
        }
        return mainWebShop === MainWebShop.JUTTU ? t('product.look.pdp.title.specific') : t('product.look.pdp.title');
    };
    const popoverMiniPDPMulti = (_jsx(Popover, { anchorEl: anchorEl, type: PopoverType.EXTENSIVE_DRAWER, onClose: handlePopoverClose, title: popoverTitle || getTitleTranslation(), extensiveDrawerElement: _jsx(MiniPDPExtensiveImageCarousel, { cdnSettings: cdnSettings, imageCdnPrefix: imageCdnPrefix, images: extensiveCarouselImages }), footerElement: _jsx(MiniPDPMultiFooter, { numberProducts: numberProducts, totalPrice: numberProducts ? totalPrice : undefined, currency: currency, handleAddToBasket: handleAddToBasket }), noFooterShadow: true, children: _jsx(ScrollableList, { variant: ScrollableListVariant.SHADOW, fullHeight: true, children: _jsxs("div", { children: [_jsx(MiniPDPMultiProductTileList, { tileIdSuffix: "-popover", products: products, selectedProductsData: selectedProductsData, wishlistProducts: wishlistProducts, sizeSelectErrors: sizeSelectErrors, isShopLanding: isShopLanding, isPDP: isPDP, handleSetError: handleSetError, onWishListToggle: onWishListToggle, onProductChange: onProductChange }), !!deliveryPromise && !!numberProducts && (_jsx(Box, { paddingInline: { base: '4', md: '8' }, paddingBlockStart: "4", paddingBlockEnd: "10", children: _jsx(MiniPDPDeliveryPromise, { deliveryPromise: deliveryPromise, boxProps: { paddingBlockEnd: { base: '1', md: '2' } } }) }))] }) }) }));
    if (isPDP) {
        return (_jsxs(_Fragment, { children: [_jsx(Breakpoint, { until: "md", children: popoverMiniPDPMulti }), _jsx(Breakpoint, { from: "md", children: _jsxs("div", { ref: tilesWrapperRef, children: [_jsx(Box, { paddingBlockEnd: { md: '4', lg: '6' }, paddingBlockStart: { md: '10' }, children: _jsx(TextAlpha, { as: "h2", variant: "subheadingXL", children: title || getTitleTranslation() }) }), _jsx(MiniPDPMultiProductTileList, { products: products, selectedProductsData: selectedProductsData, wishlistProducts: wishlistProducts, sizeSelectErrors: sizeSelectErrors, isShopLanding: isShopLanding, isPDP: isPDP, handleSetError: handleSetError, onWishListToggle: onWishListToggle, onProductChange: onProductChange }), !!numberProducts && totalPrice && (_jsx(Box, { paddingBlockStart: { md: '4' }, paddingBlockEnd: { md: '2' }, children: _jsxs(Stack, { align: "center", gap: "2", children: [_jsxs(TextAlpha, { as: "span", variant: "bodyS", children: [t('checkout:checkout.basket.total'), ":"] }), _jsx(Price, { size: PriceSize.LARGE, currency: currency, priceActual: {
                                                formattedPrice: AsNumeral.formatAsCurrency(totalPrice.totalSellPrice),
                                            }, pricePrevious: totalPrice.totalSellPrice !== totalPrice.totalStandardPrice
                                                ? {
                                                    formattedPrice: AsNumeral.formatAsCurrency(totalPrice.totalStandardPrice),
                                                }
                                                : undefined })] }) })), !!deliveryPromise && !!numberProducts && (_jsx(MiniPDPDeliveryPromise, { deliveryPromise: deliveryPromise, boxProps: { paddingBlockEnd: { base: '1', md: '2' } } })), _jsx(Box, { paddingBlockStart: { md: '4' }, children: _jsx(MiniPDPMultiCTA, { amount: numberProducts, handleAddToBasket: handleAddToBasket }) })] }) })] }));
    }
    return popoverMiniPDPMulti;
};
